// TAPINTO® Editor Frontend - Asset Browser Component
// Version: 1.0
// Date: 2025-10-01

import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Search, 
  Grid, 
  List, 
  Filter, 
  X, 
  Image as ImageIcon,
  File,
  Trash2,
  Edit3
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { useAssetStore } from '../../stores/assetStore';
import { Asset } from '../../types';
import { formatFileSize, formatRelativeTime, getImageThumbnailUrl } from '../../utils/helpers';

interface AssetBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (asset: Asset) => void;
}

export function AssetBrowser({ isOpen, onClose, onSelect }: AssetBrowserProps) {
  const {
    assets,
    isUploading,
    uploadProgress,
    searchQuery,
    fetchAssets,
    uploadAsset,
    deleteAsset,
    searchAssets,
    setSearchQuery,
  } = useAssetStore();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
    }
  }, [isOpen, fetchAssets]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      await uploadAsset(file, {
        tags: 'uploaded',
        alt_text: file.name,
      });
    }

    setShowUpload(false);
    e.target.value = ''; // Reset input
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      searchAssets(query, { type: filterType !== 'all' ? filterType : undefined });
    } else {
      fetchAssets({ type: filterType !== 'all' ? filterType : undefined });
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    if (onSelect) {
      onSelect(asset);
    } else {
      setSelectedAsset(asset);
    }
  };

  const handleAssetDelete = async (asset: Asset) => {
    if (window.confirm(`Are you sure you want to delete "${asset.filename}"?`)) {
      await deleteAsset(asset.asset_id);
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (filterType !== 'all' && asset.type !== filterType) return false;
    return true;
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Asset Browser"
      size="xl"
    >
      <div className="h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div className="flex items-center space-x-4 flex-1">
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="max-w-md"
            />
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input w-32"
            >
              <option value="all">All Types</option>
              <option value="image/jpeg">JPEG</option>
              <option value="image/png">PNG</option>
              <option value="image/svg+xml">SVG</option>
              <option value="application/pdf">PDF</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
            </Button>
            
            <Button
              onClick={() => setShowUpload(true)}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-sm text-blue-700">{uploadProgress}%</span>
            </div>
          </div>
        )}

        {/* Assets Grid/List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ImageIcon className="h-12 w-12 text-neutral-400 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                No assets found
              </h3>
              <p className="text-neutral-500 mb-4">
                {searchQuery ? 'Try adjusting your search' : 'Upload your first asset to get started'}
              </p>
              <Button onClick={() => setShowUpload(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Asset
              </Button>
            </div>
          ) : (
            <div className={
              viewMode === 'grid'
                ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
                : 'space-y-2'
            }>
              {filteredAssets.map((asset) => (
                <div
                  key={asset.asset_id}
                  className={`
                    group relative bg-white border border-neutral-200 rounded-lg overflow-hidden
                    hover:border-primary-300 hover:shadow-md transition-all cursor-pointer
                    ${viewMode === 'list' ? 'flex items-center space-x-4 p-3' : 'aspect-square'}
                  `}
                  onClick={() => handleAssetSelect(asset)}
                >
                  {/* Thumbnail */}
                  <div className={`
                    ${viewMode === 'grid' ? 'w-full h-32' : 'w-16 h-16 flex-shrink-0'}
                    bg-neutral-100 flex items-center justify-center
                  `}>
                    {asset.type.startsWith('image/') ? (
                      <img
                        src={asset.thumbnail_url || getImageThumbnailUrl(asset.cdn_url, 200, 200)}
                        alt={asset.alt_text || asset.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder-image.jpg';
                        }}
                      />
                    ) : (
                      <File className="h-8 w-8 text-neutral-400" />
                    )}
                  </div>

                  {/* Asset Info */}
                  <div className={`
                    ${viewMode === 'grid' ? 'p-3' : 'flex-1 min-w-0'}
                  `}>
                    <h4 className="font-medium text-neutral-900 truncate">
                      {asset.filename}
                    </h4>
                    <p className="text-sm text-neutral-500">
                      {formatFileSize(asset.size_bytes)}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {formatRelativeTime(asset.uploaded_at)}
                    </p>
                    {asset.usage_count > 0 && (
                      <p className="text-xs text-blue-600">
                        Used {asset.usage_count} times
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAsset(asset);
                        }}
                        className="p-1 h-8 w-8"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssetDelete(asset);
                        }}
                        className="p-1 h-8 w-8 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-500">
              {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''}
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {selectedAsset && !onSelect && (
                <Button onClick={() => {
                  onSelect?.(selectedAsset);
                  onClose();
                }}>
                  Select Asset
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <Modal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          title="Upload Asset"
          size="md"
        >
          <div className="p-6">
            <div className="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                Upload Files
              </h3>
              <p className="text-neutral-500 mb-4">
                Drag and drop files here, or click to select
              </p>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="btn btn-primary cursor-pointer"
              >
                Choose Files
              </label>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
